import {useCallback, useEffect} from "react";
import {batch, useComputed, useSignal} from "@preact/signals-react";
import {postSubmit} from "@ui-seeker/utils";

import {WEBBACK_V2_API} from "@constants";
import {useToast} from "@hooks";
import {isLoadingCommonFilters, commonFiltersValue, submittedFilters, splitKpi, isSystemBusy} from "@signals";
import {PriceEvolutionChartMetric, PriceEvolutionCurrentTabId, PriceEvolutionCurrentTabLabel, PriceEvolutionShowMinMax} from "../@signals";


export default function usePriceEvolutionChartPricing() {
	const { showToast } = useToast()
	const localLoading = useSignal(false)
	const data = useSignal({})
	const dataMinMax = useSignal({})
	const currency = useSignal({})
	const allSeries = useSignal({})
	const seriesMinMax = useSignal([])
	const colors = useSignal({})
	const isPercentage = useSignal(false)
	const isSplitBySku = useSignal(true)

	const isLoading = useComputed(() => isLoadingCommonFilters.value || localLoading.value)
	const selectedLabel = useComputed(() => isLoading.value ? null : PriceEvolutionCurrentTabLabel.value)
	const series = useComputed(() => {
		if (isLoading.value)
			return []
		return PriceEvolutionShowMinMax.value ? seriesMinMax.value : allSeries.value[PriceEvolutionCurrentTabId.value] || []
	})
	const currentData = useComputed(() => {
		if (isLoading.value)
			return []
		const currentTab = PriceEvolutionCurrentTabId.value
		return (PriceEvolutionShowMinMax.value ? dataMinMax.value[currentTab] : data.value[currentTab]) || []
	})

	const currentCurrency = useComputed(() => currency.value[PriceEvolutionCurrentTabId.value] || null)

	useEffect(() => {
		if (isLoading.value && isLoadingCommonFilters.value) {
			data.value = {}
			allSeries.value = {}
			dataMinMax.value = {}
			currency.value = {}
			handleSubmit()
		}
	}, [isLoading.value]);

	useEffect(() => {
		if (PriceEvolutionCurrentTabId.value && !isLoading.value && !(PriceEvolutionCurrentTabId.value in data.value))
			handleSubmit(true)
	}, [PriceEvolutionCurrentTabId.value]);

	const handleSubmit = (useTab=false) => {
		const filters = {...commonFiltersValue.value}
		if (useTab) {
			if (isSplitBySku.value) {
				filters.sku = [PriceEvolutionCurrentTabId.value]
			} else {
				filters.retailer = [PriceEvolutionCurrentTabId.value]
			}
		}

		if (!localLoading.value)
			localLoading.value = true
		postSubmit(`${WEBBACK_V2_API}/pricing/price-evolution/chart-pricing`, {
			...filters,
			metric: PriceEvolutionChartMetric.value
		}).then(res => batch(() => {
			localLoading.value = false
			data.value = {...data.value, ...res.chart}
			allSeries.value = {...allSeries.value, ...res.series}
			colors.value = res.colors
			currency.value = {
				...currency.value,
				[Object.keys(res.chart)[0]]: res.currency
			}

			isPercentage.value = res.isPercentage
			isSplitBySku.value = res.isSplitBySku
		})).catch(() => batch(() => {
			localLoading.value = false
			isSystemBusy.value = true
		}))

		PriceEvolutionShowMinMax.value = null
		postSubmit(`${WEBBACK_V2_API}/pricing/price-evolution/chart-min-max`, {
			...filters,
			metric: PriceEvolutionChartMetric.value
		}).then(res => batch(() => {
			PriceEvolutionShowMinMax.value = false
			dataMinMax.value = {...dataMinMax.value, ...res.chartMinMax}
			seriesMinMax.value = res.series
		})).catch(() => batch(() => {
			localLoading.value = false
			isSystemBusy.value = true
		}))
	}

	const handleChangeMetric = useCallback(newValue => batch(() => {
		localLoading.value = true
		data.value = {}
		allSeries.value = {}
		dataMinMax.value = {}
		PriceEvolutionChartMetric.value = newValue
		handleSubmit(true)
	}), [])

	const handleExcel = useCallback(() => showToast.promiseExcel(
		`${WEBBACK_V2_API}/pricing/price-evolution/chart-pricing/excel/${PriceEvolutionCurrentTabId.value}`, {
			...commonFiltersValue.value,
			metric: PriceEvolutionChartMetric.value,
			splitKpi: Array.isArray(splitKpi.value) ? splitKpi.value[0] : splitKpi.value
		}, null, {
			onError: data => {
				if (data.toString().includes('Error')) return 'Excel file size exceeded'
			}
		}), [PriceEvolutionChartMetric.value, JSON.stringify(submittedFilters.value)])

	return {
		isLoading: isLoading.value,
		allData: data.value,
		series: series.value,
		colors: colors.value,
		currency: currentCurrency.value,
		isPercentage: isPercentage.value,
		isSplitBySku: isSplitBySku.value,
		currentData: currentData.value,
		selectedLabel: selectedLabel.value,
		currentTab: PriceEvolutionCurrentTabId.value,

		handleChangeMetric,
		handleExcel: PriceEvolutionShowMinMax.value ? null : handleExcel
	}
}